import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  BrowserCacheLocation,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';

/**
 * Die Anmeldung über Entra – dieselbe Registrierung wie resad und die
 * Kalibrier-App. Die Oberfläche holt ein Token für die API (`VITE_ENTRA_SCOPE`)
 * und schickt es als Bearer; die Rollen liest sie nicht aus dem Token,
 * sondern fragt `GET /api/ich` – der Dienst entscheidet, nicht der Browser.
 */
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID;
const mandant = import.meta.env.VITE_ENTRA_TENANT_ID;

export const SCOPES = [import.meta.env.VITE_ENTRA_SCOPE].filter(Boolean);

export const eingerichtet = Boolean(clientId && mandant && SCOPES.length > 0);

export const msal = new PublicClientApplication({
  auth: {
    clientId: clientId || '00000000-0000-0000-0000-000000000000',
    authority: `https://login.microsoftonline.com/${mandant || 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    /*
     * `localStorage`, nicht `sessionStorage` – dieselbe Wahl wie in resad
     * (`apps/web/src/auth/konfiguration.ts`).
     *
     * `sessionStorage` gehört einem Tab: Wer den Rapport schließt und neu
     * öffnet, meldet sich neu an, und beim Techniker auf der Baustelle ist
     * das jedes Mal der Weg über Entra samt zweitem Faktor. Der Rapport
     * wird auf dem Telefon geführt, zwischen zwei Kunden, oft mit
     * schlechter Verbindung.
     *
     * Beides ist für JavaScript lesbar, der Unterschied im Schutz gegen XSS
     * ist also gering – der Unterschied in der Bedienung ist groß. Was
     * jemand darf, entscheidet ohnehin der Dienst am Token, nicht der
     * Browser.
     */
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
});

/**
 * Verhindert eine Endlosschleife aus Fehlschlag und Anmeldeumleitung.
 *
 * Kommt von Entra immer wieder ein Konto zurück, für das sich kein Token
 * holen lässt, liefe die Seite sonst im Kreis und niemand sähe je eine
 * Meldung. `sessionStorage`, nicht `localStorage`: Die Sperre gehört zu
 * diesem einen Anlauf, ein neuer Tab soll es wieder versuchen dürfen.
 */
const SPERRE_MS = 30_000;
const SPERRSCHLUESSEL = 'arbeitsrapporte.letzteAnmeldeumleitung';

function darfUmleiten(): boolean {
  try {
    return Date.now() - Number(sessionStorage.getItem(SPERRSCHLUESSEL) ?? 0) > SPERRE_MS;
  } catch {
    // Privater Modus oder blockierter Speicher: lieber einmal umleiten.
    return true;
  }
}

function merkeUmleitung(): void {
  try {
    sessionStorage.setItem(SPERRSCHLUESSEL, String(Date.now()));
  } catch {
    // Ohne Speicher keine Sperre – dann greift nur der Fehlertext.
  }
}

/**
 * Fehler aus dem versteckten Rahmen, die dasselbe bedeuten wie
 * `InteractionRequiredAuthError`: still geht es nicht mehr weiter.
 *
 * MSAL holt ein neues Token zuerst über den Refresh-Token. Ist der abgelaufen
 * – bei einer SPA begrenzt Entra ihn auf 24 Stunden –, öffnet es einen
 * versteckten iframe und ruft darin `authorize` mit `prompt=none` auf.
 * Braucht Entra dann doch eine Eingabe, antwortet es nicht mit einer
 * Umleitung zurück auf `blank.html`, sondern mit einer Seite, die den
 * **ganzen** Tab auf die Anmeldung schicken will. Der Browser lässt das aus
 * dem Rahmen heraus nicht zu; der Rahmen bleibt stehen, und MSAL meldet
 * einen `BrowserAuthError` – **keinen** `InteractionRequiredAuthError`.
 *
 * Mit `sessionStorage` fiel das kaum auf: Eine Sitzung überlebte selten
 * 24 Stunden. Mit `localStorage` ist genau das der Normalfall am zweiten
 * Tag, und ohne diese Liste bliebe die Oberfläche bei „Anmeldung wird
 * geprüft …" stehen, statt zur Anmeldung zu schicken.
 *
 * Die Liste ist bewusst eine Aufzählung und kein „alles, was
 * `BrowserAuthError` heißt": Ein blockierter Speicher oder eine fehlende
 * Krypto-API sind ebenfalls `BrowserAuthError`, und dagegen hilft keine
 * Umleitung – die liefe endlos.
 */
const RAHMEN_BRAUCHT_ANMELDUNG: ReadonlySet<string> = new Set([
  // Der Rahmen kam nie zurück – der Fall aus der Beschreibung oben. Beide
  // Codes, weil MSAL 4 den Ablauf im Rahmen noch `monitorWindowTimeout`
  // nennt und daneben schon `timedOut` kennt.
  BrowserAuthErrorCodes.monitorWindowTimeout,
  BrowserAuthErrorCodes.timedOut,
  // Er kam zurück, aber ohne verwertbare Antwort.
  BrowserAuthErrorCodes.hashEmptyError,
  BrowserAuthErrorCodes.hashDoesNotContainKnownProperties,
  BrowserAuthErrorCodes.emptyWindowError,
  BrowserAuthErrorCodes.iframeClosedPrematurely,
  // Der zugehörige Anfragezustand fehlt – ohne ihn ist still nichts zu holen.
  BrowserAuthErrorCodes.noTokenRequestCacheError,
]);

/** Heißt dieser Fehler „schick den Nutzer zur Anmeldung"? */
export function brauchtAnmeldung(fehler: unknown): boolean {
  if (fehler instanceof InteractionRequiredAuthError) {
    return true;
  }

  return fehler instanceof BrowserAuthError && RAHMEN_BRAUCHT_ANMELDUNG.has(fehler.errorCode);
}

export async function holeToken(): Promise<string> {
  const konto = msal.getActiveAccount() ?? msal.getAllAccounts()[0];

  if (!konto) {
    throw new Error('Nicht angemeldet.');
  }

  try {
    /*
     * Die stille Erneuerung laeuft in einem versteckten iframe. Der laedt
     * `blank.html` statt der Anwendung – eine leere Seite ohne Skript, die
     * als einzige im eigenen Rahmen erlaubt ist (`http/app.ts`,
     * X-Frame-Options). Die Anwendung selbst in einen Rahmen zu laden
     * waere langsam und ist verboten.
     */
    const ergebnis = await msal.acquireTokenSilent({
      scopes: SCOPES,
      account: konto,
      redirectUri: `${window.location.origin}/blank.html`,
    });

    return ergebnis.accessToken;
  } catch (fehler) {
    // Nur, wenn nicht gerade eben schon eine Umleitung lief – sonst fliegt
    // lieber der Fehler, und der Nutzer sieht, woran es liegt.
    if (brauchtAnmeldung(fehler) && darfUmleiten()) {
      merkeUmleitung();

      /*
       * Ohne eigenes `redirectUri`: Diese Umleitung verlässt den Tab und
       * muss auf der Oberfläche zurückkommen, nicht auf `blank.html`.
       */
      await msal.acquireTokenRedirect({ scopes: SCOPES, account: konto });
    }

    throw fehler;
  }
}
